/**
 * Sob que chave mestra este portal está cifrando — dito por extenso, para quem administra.
 *
 * Este cartão é um dos três lugares onde o modo provisório é obrigado a aparecer. Os outros dois
 * são `GET /api/saude`, que publica só a palavra por ser o único endereço público, e a auditoria
 * de cada segredo gravado, que é o único dos três que sobrevive ao tempo. Três porque um só é um
 * lugar de onde a informação some: o painel de saúde é lido por monitor e não por pessoa, e a
 * auditoria só é lida depois que algo aconteceu.
 *
 * **Nada aqui está atrás de um ícone.** A custódia provisória é uma frase inteira, na cor do
 * estado, dizendo o que muda na prática — "comprometer o servidor entrega todas as credenciais" —
 * e quantos segredos já foram gravados assim. Um triângulo amarelo com `title` seria a versão
 * bonita de esconder: quem opera o portal descobriria o problema lendo o código.
 *
 * A falha da consulta **também** é um estado de atenção. Não saber sob que chave o cofre cifra
 * nunca pode ser mostrado como se estivesse tudo certo.
 */

import { Aviso, Cartao, Chip } from "../ui";
import {
  CHIP_DA_CUSTODIA,
  ROTULO_DA_CUSTODIA,
  TEXTO_DA_CUSTODIA,
  mensagemDoCofre,
  useCustodiaDoCofre,
} from "../../nucleo/cofre";

export interface PropsDaCustodia {
  className?: string;
}

export function CartaoDaCustodia({ className }: PropsDaCustodia) {
  const consulta = useCustodiaDoCofre();

  if (consulta.isPending) {
    return (
      <Cartao
        className={className}
        titulo="Proteção das credenciais"
        data-testid="custodia-do-cofre"
        data-estado-da-custodia="consultando"
      >
        <p className="cartao__texto" role="status">
          Consultando a proteção das credenciais…
        </p>
      </Cartao>
    );
  }

  if (consulta.isError) {
    return (
      <Cartao
        className={className}
        titulo="Proteção das credenciais"
        data-testid="custodia-do-cofre"
        data-estado-da-custodia="desconhecida"
        acessorio={<Chip estado="erro">desconhecida</Chip>}
      >
        <Aviso tom="erro" data-testid="falha-da-custodia">
          <b>Não foi possível confirmar como as credenciais estão protegidas.</b>{" "}
          {mensagemDoCofre(consulta.error)} Enquanto isso, trate as credenciais guardadas como se
          estivessem com a proteção provisória.
        </Aviso>
      </Cartao>
    );
  }

  const custodia = consulta.data;

  return (
    <Cartao
      className={className}
      titulo="Proteção das credenciais"
      data-testid="custodia-do-cofre"
      /* O estado sai como atributo do CARTÃO, e com nome próprio: `data-estado` já é do `Chip` e
         carrega a cor ("atencao"), não a custódia. São coisas diferentes, e confundi-las faria a
         tela afirmar "atenção" onde o dado diz "provisória". */
      data-estado-da-custodia={custodia.estado}
      acessorio={
        <Chip estado={CHIP_DA_CUSTODIA[custodia.estado]}>
          {ROTULO_DA_CUSTODIA[custodia.estado]}
        </Chip>
      }
    >
      {custodia.exige_atencao ? (
        <Aviso
          tom={custodia.estado === "indisponivel" ? "erro" : "atencao"}
          className="mb-3"
          data-testid="custodia-exige-atencao"
        >
          <b>
            {custodia.estado === "indisponivel"
              ? "O cofre está parado."
              : "As credenciais estão com a proteção provisória."}
          </b>{" "}
          {TEXTO_DA_CUSTODIA[custodia.estado]}
        </Aviso>
      ) : (
        <p className="cartao__texto mb-3">{TEXTO_DA_CUSTODIA[custodia.estado]}</p>
      )}

      <dl className="grid gap-4 sm:grid-cols-2 m-0">
        <div>
          <dt className="campo__rotulo">Credenciais guardadas</dt>
          <dd className="m-0 text-sm numero-tabular" data-testid="segredos-vivos">
            {custodia.segredos_vivos}
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">Com a proteção provisória</dt>
          <dd
            className="m-0 text-sm numero-tabular"
            data-testid="segredos-sob-provisoria"
          >
            {custodia.segredos_sob_custodia_provisoria}
            {custodia.segredos_sob_custodia_provisoria > 0 ? (
              <span className="dado-secundario">
                serão protegidas de novo quando a chave definitiva for ativada
              </span>
            ) : null}
          </dd>
        </div>
      </dl>

      {/* A frase do servidor entra INTEIRA e além da nossa: ela é a única que sabe qual variável
          foi configurada e o que exatamente falhou. Fica recolhida, e não apagada: é diagnóstico
          para quem pode corrigir, não o texto de uso do cartão. */}
      <details className="mt-4" data-testid="detalhes-tecnicos-da-custodia">
        <summary className="campo__rotulo cursor-pointer">Detalhes técnicos</summary>
        <p className="cartao__texto mt-2 mb-2" data-testid="detalhe-da-custodia">
          {custodia.detalhe}
        </p>
        {/* A referência — id da chave gerenciada ou impressão digital da provisória. Nunca
            material, e nunca em `/api/saude`: contar ao mundo QUAL chave é assunto de quem já
            está autenticado. */}
        <p className="m-0 text-sm break-all">
          Chave em uso:{" "}
          <code data-testid="referencia-da-custodia">{custodia.referencia || "—"}</code>
        </p>
      </details>
    </Cartao>
  );
}
